import {action} from 'mobx';
import s from '../s';
import * as emitter from '../../global/emitter';
import {hideAllModals} from '../../util';
import * as DocSizes from '../components/doc-sizes-and-categories/doc-size-pulse';
import {groupLinoOptionsByText} from '../print-options';
import {LinoCustomization} from '../i';
import {scrollToPreview} from './preview-scroll';
import {sizeAfterSchoolChange} from './doc-size';

// The lino (the embossed school name: an Entities row of EntityTypeID 7) selection, shared by both product page layouts. Moved
// unchanged from components/lino/lino.tsx; the control's Lino keeps only its display state (titles, the collapse) and calls these.

/** The Icon types (83, 84, 87, 91, 92): their lino labels carry the colour. */
export const isIconLinoProduct = (): boolean => s.prod.ProductTypeID === 83 || s.prod.ProductTypeID === 84 || s.prod.ProductTypeID === 87 || s.prod.ProductTypeID === 92 || s.prod.ProductTypeID === 91;

/** Icon types label each lino by its own title unless the choices span more than one grad school. */
export const linoIconUi = (): boolean => {
    const uniqueGradSchoolIds = new Set(s.linoChoices.options.flatMap(item => item.gradSchoolIds ?? []).filter(id => id > 0));
    return isIconLinoProduct() && !(uniqueGradSchoolIds.size > 1);
};

export const linoHasMultipleColors = (): boolean => new Set(s.prod.linos.map(item => item.desc4)).size > 1;

/**
 * The lino list's display titles and order (Lino's first render): each lino's _title, then the base linos first, the linos without
 * grad schools next, the grad-school linos last, each group by _title (the base lino stays first so the default pick is unchanged).
 */
export const prepareLinos = action((iconUi: boolean = linoIconUi()): void => {
    const isMultipleColors = linoHasMultipleColors();

    // chc-223 - compute the dynamic display title once per lino into _title, then sort on _title (base lino stays first so the default dropdown selection is unchanged)
    for (const m of s.prod.linos) {
        const _t = iconUi ? m.title.trim() : m.isBase || !m.gradSchoolIds || m.gradSchoolIds.length < 1 || m.gradSchoolIds[0] < 1 ? m.title : m.title.replace(s.si.InstName, '').trim() == '' ? m.title : m.title.replace(s.si.InstName, '').trim();
        m._title = `${_t}${isMultipleColors && !m.title.includes(m.desc4) ? ` - ${m.desc4}` : ''}`;
    }
    // sort rank: base linos first, then linos with no grad schools, then grad-school linos; each group sorted by _title
    const _linoSortRank = (m: any): number => (m.isBase ? 0 : !m.gradSchoolIds || m.gradSchoolIds.length < 1 || !m.gradSchoolIds.some((id: number) => id > 0) ? 1 : 2);
    s.prod.linos = s.prod.linos.slice().sort((a, b) => _linoSortRank(a) - _linoSortRank(b) || a._title.localeCompare(b._title));
});

/** Back to the product's own lino. */
export const restoreOriginalLino = action((origLinoName: string = s.prod.orig_pcs.Base_LinoName): void => {
    s.ps.lino_id = s.preferredLinoId(s.prod.LinoID);
    s.ps.lino_base_name = s.prod.linos.find(m => m.id === s.ps.lino_id)?.name || origLinoName;
    s.ps.lino_text = s.ps.lino_base_name;
    s.ps.lino_dyf_color = 'Gold';
    s.ps.lino_is_dyf = false;
    s.ps.ep_id = 0;
    s.ui.update_pricing = true;
    s.ui.doRender = true;
    s.pulse();
});

/**
 * Picks a lino: its grad school becomes the current one, the document sizes are re-run for it (the shopper's size stays when the new
 * school offers it, else its size for the same degree, else the first size of the new list: sizeAfterSchoolChange), the page
 * re-prices and re-renders, and lino_changed tells a mounted seal list to re-pick its seal. Returns the lino.
 */
export const selectLino = action((_id: number): LinoCustomization | undefined => {
    const m = s.prod.linos.find(m => m.id === s.preferredLinoId(_id));
    if (m) {
        s.ps.lino_base_name = m.name;
        s.ps.lino_is_dyf = false;
        s.ps.lino_id = m.id;
        s.ps.lino_text = m.title;
        s.ps.ep_id = 0;

        const previousSize = s.si.ProductDocSizes?.find(x => x.Id === s.docsizes_selected_id);
        s.currentGradSchoolID = m.gradSchoolIds[0];

        DocSizes.PulseChange(false);

        const size = sizeAfterSchoolChange(s.docsizes_available, previousSize, Date.now());

        if (size) {
            DocSizes.SetDocSize(size.Id);
        }
    }

    s.ui.update_pricing = true;
    s.ui.doRender = true;
    s.pulse();
    emitter.eventEmitter.dispatch(emitter.EventType.AB, 'lino_changed');

    scrollToPreview('prod-header');

    return m;
});

/** The lino picker's dropdown value: -1 is the product's own lino, anything else a lino id. */
export const selectLinoOption = (id: number): void => {
    if (id === -1) {
        restoreOriginalLino();
    } else {
        selectLino(id);
    }
};

/** The degree-type modal's answer after a lino pick. */
export const selectLinoDocSize = action((_docSizeId: number = -1): void => {
    const docSizeMatch = s.si.ProductDocSizes?.find(item => item.Id == _docSizeId);
    if (docSizeMatch) {
        DocSizes.SetDocSize(docSizeMatch.Id);
    }

    s.ui.update_pricing = true;
    s.ui.doRender = true;
    s.pulse();
    hideAllModals();

    scrollToPreview('prod-header');
});

export const setLinoColor = action((color: string): void => {
    s.ps.lino_dyf_color = color.toString().trim().toLowerCase();
    s.ui.update_pricing = true;
    s.ui.doRender = true;
    s.pulse();
});

/** No lino (products whose own setup has none): the control's colour selects go back to their first option. */
export const removeLino = action((): void => {
    s.ps.lino_id = -1;
    for (const id of ['lino-dyf-1', 'lino-dyf-2', 'lino-dyf-3', 'lino-dyf-4', 'lino-dyf-5']) {
        const f = document.getElementById(id);
        if (f) {
            f.getElementsByTagName('option')[0].selected = true;
        }
    }
    s.ui.update_pricing = true;
    s.ui.doRender = true;
    s.pulse();
});

// ---- view model ------------------------------------------------------------------------------------------------------

/** product.tsx's mount rule for the lino section. */
export const linoSectionMounted = (): boolean => !s.IsPortal3 && !s.ps.is_uv_print && !!s.prod && !!s.prod.linos && !(s.prod.linos.length === 1 && s.prod.linos[0].id === s.prod.LinoID) && (s.prod.linos.length > 1 || (s.prod.linos.length > 0 && s.prod.LinoID < 1) || s.prod.ProductID === 88937);

/** The section shows: mounted, and Lino's own guard. */
export const linoSectionShown = (): boolean => linoSectionMounted() && !!s.ui.show_cust && !!s.prod && !!s.prod.linos;

/**
 * Linos that belong to a frame-part-category (fpc_id > 0), grouped by category. Each group renders as its own dropdown titled with
 * the category name (fpc_name). Linos without a category (fpc_id <= 0) are left to the existing display logic.
 */
export const linoCategories = (): {id: number; name: string; items: any[]}[] => {
    if (!s.prod || !s.prod.linos) {
        return [];
    }
    const cats = new Map<number, {id: number; name: string; items: any[]}>();
    for (const m of s.linoChoices.options) {
        if (m.isBase || !(m.fpc_id > 0)) {
            continue;
        }
        let cat = cats.get(m.fpc_id);
        if (!cat) {
            cat = {id: m.fpc_id, name: m.fpc_name || '', items: []};
            cats.set(m.fpc_id, cat);
        }
        cat.items.push(m);
    }
    const arr = Array.from(cats.values());
    arr.forEach(c => c.items.sort((a, b) => a.title.localeCompare(b.title)));
    arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr;
};

/** Linos that do NOT belong to a category (fpc_id <= 0): the existing (non-category) lino display. */
export const uncategorizedLinos = (): any[] => {
    if (!s.prod || !s.prod.linos) {
        return [];
    }
    return s.linoChoices.options.filter(m => !(m.fpc_id > 0));
};

/** Uncategorized linos grouped by text. When the same text comes in more than one color, the dropdown lists each text once and a color dropdown picks the color. */
export const linoTextGroups = () => groupLinoOptionsByText(uncategorizedLinos());

/** The colours the selected mat (and lino) allow: the control offers a lino colour select when more than one is. */
export const linoColorOptionsCount = (): number => {
    let i = s.ps.ins_gold_allowed ? 1 : 0;
    i += s.ps.ins_black_allowed ? 1 : 0;
    i += s.ps.ins_silver_allowed ? 1 : 0;
    return i;
};

/** The control's lino colour select (a lino other than the product's own, a single choice, more than one allowed colour). */
export const linoColorSelectShown = (): boolean =>
    linoColorOptionsCount() > 1 && s.preferredLinoId(s.prod.LinoID) !== s.preferredLinoId(s.ps.lino_id) && s.linoChoices.options.length < 2 && s.prod.ProductID !== 75593 && s.prod.ProductID !== 75595 && s.prod.ProductID !== 75591 && s.prod.ProductID !== 75589 && s.prod.ProductID !== 88937;

/** The control's "Remove ..." button: a lino is picked on a product whose own setup has none. */
export const linoRemovable = (): boolean => s.ps.lino_id > 0 && s.prod.LinoID < 1;

/** The lino artwork the page shows on the mat colour (lino.tsx's preview under the picker). */
export const linoArtworkSrc = (cdnDomain: string | undefined, linoId: number = s.ps.lino_id): string => `https://${cdnDomain}/images/entities/all/${linoId}_original.png`;

/**
 * A lino setup the redesign's single school select cannot carry, so its school step keeps the control's Lino: store 205164's two
 * selects, product 88937's custom text, category dropdowns (fpc), the text + colour selects, or a single choice (whose pick can
 * bring up the control's colour select, linoColorSelectShown).
 */
export const linoNeedsControlPicker = (): boolean => s.si.StoreID === 205164 || s.prod.ProductID === 88937 || linoCategories().length > 0 || linoTextGroups().hasColors || s.linoChoices.options.length < 2;
